import { useEffect, useMemo, useState } from "react";
import { Plus, Edit3, Trash2, Loader2, Search, Sparkles, Eye } from "lucide-react";
import AdminShell from "../components/AdminShell";
import { useI18n } from "../i18n/I18nContext";
import { api } from "../lib/nailApi";
import { Toast, FieldInput, FieldTextarea, FieldSelect, FieldToggle, DialogShell, ConfirmDialog } from "../components/AdminUI";
import ImageUploader from "../components/ImageUploader";

const TOKEN_KEY = "nailEvent.adminToken";
const EMPTY = {
  title: "", summary: "", body: "", featured_image_url: "",
  category: "Business", language: "vi", author: "", publish_date: "",
  status: "draft", featured: false, visible_ai: true,
  seo_title: "", seo_description: "", seo_keywords: "",
};

export default function AdminArticles() {
  const { lang } = useI18n();
  const token = sessionStorage.getItem(TOKEN_KEY) || "";
  const L = (vi, en) => (lang === "en" ? en : vi);
  const headers = useMemo(() => ({ "X-Admin-Token": token }), [token]);

  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ categories: [], languages: [] });
  const [q, setQ] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterCat, setFilterCat] = useState("");
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [toast, setToast] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(null);

  const flash = (m) => { setToast(m); setTimeout(() => setToast(""), 2000); };

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (filterStatus) params.status = filterStatus;
      if (filterCat) params.category = filterCat;
      const [r1, r2] = await Promise.all([
        api.get("/admin/articles", { headers, params }),
        api.get("/admin/articles/meta", { headers }),
      ]);
      setItems(r1.data.articles || []);
      setMeta(r2.data);
    } catch (err) { flash(err?.response?.data?.detail || "Error"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [filterStatus, filterCat]); // eslint-disable-line

  const save = async (payload, id) => {
    try {
      if (id) await api.put(`/admin/articles/${id}`, payload, { headers });
      else await api.post("/admin/articles", payload, { headers });
      setEditing(null); flash(L("Đã lưu", "Saved")); load();
    } catch (err) { flash(err?.response?.data?.detail || "Error"); }
  };
  const remove = async (id) => {
    try { await api.delete(`/admin/articles/${id}`, { headers }); setConfirmDelete(null); flash(L("Đã xóa", "Deleted")); load(); }
    catch (err) { flash(err?.response?.data?.detail || "Error"); }
  };

  return (
    <AdminShell
      testid="admin-articles"
      subtitle={L("Trung Tâm Học Tập", "Learning Center")}
      title={L("Bài Viết", "Articles")}
      actions={
        <button type="button" onClick={() => setEditing({ mode: "create", data: { ...EMPTY } })} data-testid="article-add-btn"
          className="inline-flex items-center gap-1.5 bg-espresso text-linen rounded-full px-4 py-2 text-sm font-medium hover:bg-espresso-hover">
          <Plus className="w-4 h-4" /> {L("Bài viết mới", "New Article")}
        </button>
      }
    >
      <div className="max-w-[1400px]">
        <Toast message={toast} testId="article-toast" />

        <div className="bg-white border border-espresso/10 rounded-3xl p-4 mb-6 grid grid-cols-1 md:grid-cols-4 gap-3">
          <form onSubmit={(e) => { e.preventDefault(); load(); }} className="md:col-span-2 flex items-center gap-2 border border-espresso/10 rounded-xl px-3 py-2">
            <Search className="w-4 h-4 text-espresso-muted" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={L("Tìm tiêu đề, tóm tắt...", "Search title, summary...")} data-testid="article-search"
              className="flex-1 bg-transparent text-sm focus:outline-none" />
          </form>
          <select value={filterCat} onChange={(e) => setFilterCat(e.target.value)} data-testid="article-cat-filter" className="border border-espresso/10 rounded-xl px-3 py-2 text-sm bg-white">
            <option value="">{L("Tất cả danh mục", "All categories")}</option>
            {meta.categories?.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} data-testid="article-status-filter" className="border border-espresso/10 rounded-xl px-3 py-2 text-sm bg-white">
            <option value="">{L("Tất cả trạng thái", "All statuses")}</option>
            <option value="draft">Draft</option>
            <option value="published">Published</option>
            <option value="archived">Archived</option>
          </select>
        </div>

        {loading ? (
          <div className="py-16 text-center"><Loader2 className="w-5 h-5 animate-spin inline text-espresso-muted" /></div>
        ) : items.length === 0 ? (
          <div className="bg-white border border-dashed border-espresso/20 rounded-3xl p-12 text-center text-espresso-muted">
            {L("Chưa có bài viết. Bài đăng công khai sẽ hiển thị trên Trung Tâm Học Tập.", "No articles yet. Published articles appear on the public Learning Center.")}
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map((a) => (
              <div key={a.id} data-testid={`article-card-${a.id}`} className="bg-white border border-espresso/10 rounded-3xl overflow-hidden flex flex-col">
                <div className="aspect-video bg-linen-100 overflow-hidden">
                  {a.featured_image_url ? <img src={a.featured_image_url} alt={a.title} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center font-serif text-warmgray-500 text-3xl">{a.title?.[0]}</div>}
                </div>
                <div className="p-5 flex flex-col gap-2 flex-1">
                  <div className="flex items-center gap-2">
                    <span className={`text-[10px] uppercase tracking-[0.18em] font-semibold px-2 py-0.5 rounded-full ${a.status === "published" ? "bg-emerald2/10 text-emerald2" : "bg-warmgray-100 text-warmgray-700"}`}>{a.status}</span>
                    {a.featured && <Sparkles className="w-3.5 h-3.5 text-gold-500" />}
                    <span className="text-[10px] text-espresso-muted ml-auto">{a.category} · {a.language?.toUpperCase()}</span>
                  </div>
                  <div className="font-serif text-lg text-espresso line-clamp-2">{a.title}</div>
                  {a.summary && <p className="text-sm text-espresso-soft line-clamp-2 flex-1">{a.summary}</p>}
                  <div className="text-[10px] text-espresso-muted">{a.author || "—"} · {a.publish_date || ""}</div>
                  <div className="flex items-center gap-2 mt-1">
                    {a.status === "published" && (
                      <a href={`/learn/${a.slug}`} target="_blank" rel="noopener noreferrer" data-testid={`article-view-${a.id}`} className="p-1.5 rounded-lg hover:bg-linen-100 text-espresso-soft hover:text-espresso"><Eye className="w-4 h-4" /></a>
                    )}
                    <button type="button" onClick={() => setEditing({ mode: "edit", data: a })} data-testid={`article-edit-${a.id}`} className="p-1.5 rounded-lg hover:bg-linen-100 text-espresso-soft hover:text-espresso"><Edit3 className="w-4 h-4" /></button>
                    <button type="button" onClick={() => setConfirmDelete(a)} data-testid={`article-delete-${a.id}`} className="p-1.5 rounded-lg hover:bg-[#F5DADA] text-espresso-soft hover:text-[#7A2E2E]"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {editing && (
        <ArticleEditDialog mode={editing.mode} initial={editing.data} meta={meta} onClose={() => setEditing(null)}
          onSave={(p) => save(p, editing.mode === "edit" ? editing.data.id : null)} L={L} />
      )}
      {confirmDelete && (
        <ConfirmDialog title={L("Xóa bài viết?", "Delete article?")} body={L("Hành động này không thể hoàn tác.", "This cannot be undone.")}
          onCancel={() => setConfirmDelete(null)} onConfirm={() => remove(confirmDelete.id)} L={L} />
      )}
    </AdminShell>
  );
}

function ArticleEditDialog({ mode, initial, meta, onClose, onSave, L }) {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    if (!form.title?.trim()) return;
    setSaving(true);
    await onSave({
      title: form.title.trim(),
      summary: form.summary || null, body: form.body || "",
      featured_image_url: form.featured_image_url || null,
      category: form.category || null, language: form.language || "vi",
      author: form.author || null, publish_date: form.publish_date || null,
      status: form.status || "draft",
      featured: !!form.featured, visible_ai: !!form.visible_ai,
      seo_title: form.seo_title || null,
      seo_description: form.seo_description || null,
      seo_keywords: form.seo_keywords || null,
    });
    setSaving(false);
  };
  return (
    <DialogShell title={mode === "edit" ? L("Chỉnh sửa Bài Viết", "Edit Article") : L("Bài Viết Mới", "New Article")}
      onClose={onClose} onSubmit={submit} saving={saving} testIdPrefix="article" maxWidth="max-w-4xl">
      <div className="grid md:grid-cols-2 gap-4">
        <FieldInput label={L("Tiêu đề *", "Title *")} value={form.title} onChange={(v) => set("title", v)} testId="article-title" required className="md:col-span-2" />
        <FieldTextarea label={L("Tóm tắt", "Summary")} value={form.summary} onChange={(v) => set("summary", v)} rows={2} testId="article-summary" className="md:col-span-2" />
        <div className="md:col-span-2">
          <ImageUploader
            label={L("Ảnh đại diện", "Featured image")}
            value={form.featured_image_url}
            onChange={(url) => set("featured_image_url", url)}
            subfolder="articles"
            aspect="landscape"
            testId="article-image"
            hint={L("Ảnh ngang (16:9) hiển thị đẹp nhất. Tối đa 10MB.", "Landscape (16:9) works best. Max 10MB.")}
          />
        </div>
        <FieldTextarea label={L("Nội dung (Markdown / HTML đơn giản)", "Body (Markdown / simple HTML)")} value={form.body} onChange={(v) => set("body", v)} rows={12} testId="article-body" className="md:col-span-2" />
        <FieldSelect label={L("Danh mục", "Category")} value={form.category || ""} onChange={(v) => set("category", v)} testId="article-category"
          options={meta.categories.map((c) => [c, c])} />
        <FieldSelect label={L("Ngôn ngữ", "Language")} value={form.language} onChange={(v) => set("language", v)} testId="article-lang"
          options={[["vi", "Tiếng Việt"], ["en", "English"], ["es", "Español"], ["zh", "中文"], ["ko", "한국어"]]} />
        <FieldInput label={L("Tác giả", "Author")} value={form.author} onChange={(v) => set("author", v)} testId="article-author" />
        <FieldInput label={L("Ngày đăng", "Publish date")} value={form.publish_date} onChange={(v) => set("publish_date", v)} placeholder="2026-03-01" testId="article-pubdate" />
        <FieldSelect label={L("Trạng thái", "Status")} value={form.status} onChange={(v) => set("status", v)} testId="article-status"
          options={[["draft", "Draft"], ["published", "Published"], ["archived", "Archived"]]} />
        <div className="grid grid-cols-2 gap-2">
          <FieldToggle label={L("Nổi bật", "Featured")} checked={form.featured} onChange={(v) => set("featured", v)} testId="article-featured" />
          <FieldToggle label={L("AI dùng", "Visible to AI")} checked={form.visible_ai} onChange={(v) => set("visible_ai", v)} testId="article-vis-ai" />
        </div>
        <div className="md:col-span-2 border-t border-warmgray-100 pt-3">
          <p className="text-[10px] uppercase tracking-[0.22em] font-semibold text-warmgray-700 mb-2">SEO</p>
          <div className="grid md:grid-cols-2 gap-3">
            <FieldInput label="SEO title" value={form.seo_title} onChange={(v) => set("seo_title", v)} testId="article-seo-title" />
            <FieldInput label="SEO keywords" value={form.seo_keywords} onChange={(v) => set("seo_keywords", v)} testId="article-seo-keywords" />
            <FieldTextarea label="SEO description" value={form.seo_description} onChange={(v) => set("seo_description", v)} rows={2} testId="article-seo-desc" className="md:col-span-2" />
          </div>
        </div>
      </div>
    </DialogShell>
  );
}
